import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "react-toastify";
import CameraIcon from "../../assets/image/camera.svg";

const ORIENTATION_CONFIG = {
  portrait: {
    aspect: 4 / 5,
    width: 720,
    height: 900,
    previewClass: "aspect-[4/5] max-w-[320px]",
    cardPreviewClass: "aspect-[4/5]",
    emptyClass: "h-32 w-60",
  },
  landscape: {
    aspect: 4 / 3,
    width: 1280,
    height: 960,
    previewClass: "aspect-[4/3] w-full max-w-[480px]",
    cardPreviewClass: "aspect-[4/3]",
    emptyClass: "h-40 w-60",
  },
};

const stopStream = (stream) => {
  stream?.getTracks?.().forEach((track) => track.stop());
};

const getVideoConstraints = (facingMode, orientation = "portrait") => {
  const config = ORIENTATION_CONFIG[orientation] || ORIENTATION_CONFIG.portrait;
  const facing =
    facingMode === "environment"
      ? { facingMode: { ideal: "environment" } }
      : { facingMode: "user" };

  return {
    video: {
      ...facing,
      aspectRatio: { ideal: config.aspect },
      width: { ideal: config.width },
      height: { ideal: config.height },
    },
    audio: false,
  };
};

const captureFramedVideo = (video, targetAspect) => {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  if (!sourceWidth || !sourceHeight) return null;

  let cropX = 0;
  let cropY = 0;
  let cropWidth = sourceWidth;
  let cropHeight = sourceHeight;

  const sourceAspect = sourceWidth / sourceHeight;

  if (sourceAspect > targetAspect) {
    cropWidth = Math.round(sourceHeight * targetAspect);
    cropX = Math.round((sourceWidth - cropWidth) / 2);
  } else if (sourceAspect < targetAspect) {
    cropHeight = Math.round(sourceWidth / targetAspect);
    cropY = Math.round((sourceHeight - cropHeight) / 2);
  }

  const canvas = document.createElement("canvas");
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(
    video,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return canvas;
};

const CameraCapture = ({
  value,
  onCapture,
  onRemove,
  disabled = false,
  variant = "circle",
  modalTitle = "Take photo",
  emptyLabel = "Take Photo",
  fileNamePrefix = "photo",
  facingMode = "user",
  orientation = "portrait",
  className = "",
  previewMeta = null,
  bareTrigger = false,
}) => {
  const config = ORIENTATION_CONFIG[orientation] || ORIENTATION_CONFIG.portrait;
  const modalTitleId = useId();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const closeCamera = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsOpen(false);
  }, []);

  useEffect(() => () => stopStream(streamRef.current), []);

  useEffect(() => {
    if (!isOpen || !streamRef.current || !videoRef.current) return undefined;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
    return undefined;
  }, [isOpen]);

  const openCamera = async () => {
    if (disabled) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("No camera found");
      return;
    }

    const tryOpen = async (mode) =>
      navigator.mediaDevices.getUserMedia(getVideoConstraints(mode, orientation));

    try {
      let stream;
      try {
        stream = await tryOpen(facingMode);
      } catch (firstErr) {
        if (facingMode === "environment") {
          stream = await tryOpen("user");
        } else {
          throw firstErr;
        }
      }
      streamRef.current = stream;
      setIsOpen(true);
    } catch (err) {
      console.error(err);
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        toast.error("Camera permission denied");
        return;
      }
      toast.error("No camera found");
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = captureFramedVideo(video, config.aspect);
    if (!canvas) {
      toast.error("Camera is not ready yet. Please try again.");
      return;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Could not capture photo.");
          return;
        }
        const file = new File([blob], `${fileNamePrefix}-${Date.now()}.jpg`, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
        onCapture?.(file);
        closeCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  const emptyTriggerClassName =
    variant === "circle"
      ? "flex h-40 w-40 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed bg-white disabled:cursor-not-allowed disabled:opacity-60"
      : `flex ${config.emptyClass} cursor-pointer flex-col items-center justify-center rounded-lg px-2 text-center disabled:cursor-not-allowed disabled:opacity-60 ${
          bareTrigger ? "border-0 bg-transparent" : "border-2 border-dashed bg-white"
        }`;

  const previewCardClassName = bareTrigger
    ? "group relative w-[168px] overflow-hidden rounded-xl bg-transparent sm:w-[188px]"
    : "group relative w-[168px] overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/5 transition hover:shadow-lg hover:ring-black/10 sm:w-[188px]";

  const renderTrigger = () => {
    if (previewUrl && variant === "card" && previewMeta) {
      return (
        <div className={previewCardClassName}>
          {onRemove ? (
            <button
              type="button"
              aria-label={`Remove ${previewMeta.label}`}
              className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-base leading-none text-gray-500 shadow-md backdrop-blur-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              ×
            </button>
          ) : null}
          <button
            type="button"
            disabled={disabled}
            onClick={openCamera}
            className={`relative block w-full bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 ${config.cardPreviewClass}`}
            aria-label={`Retake ${previewMeta.label}`}
          >
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-2 pb-2 pt-10">
              <p className="truncate text-center text-[11px] font-medium text-white drop-shadow-sm">
                {previewMeta.name}
              </p>
              {previewMeta.size ? (
                <p className="text-center text-[10px] text-white/85">{previewMeta.size}</p>
              ) : null}
            </div>
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={openCamera}
        className={`${emptyTriggerClassName} ${className}`}
        aria-label={modalTitle}
      >
        {previewUrl && variant === "circle" ? (
          <img
            src={previewUrl}
            alt="Captured"
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          <span className="flex flex-col items-center gap-2 px-2 text-center text-sm text-gray-600">
            <img src={CameraIcon} alt="" className="h-10 w-10" />
            {emptyLabel}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {renderTrigger()}

      {isOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          onClick={closeCamera}
        >
          <div
            className={`w-full overflow-hidden rounded-2xl bg-white shadow-xl ${
              orientation === "landscape" ? "max-w-2xl" : "max-w-md"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-5 py-4">
              <h3 id={modalTitleId} className="text-lg font-UbuntuMedium text-gray-900">
                {modalTitle}
              </h3>
            </div>
            <div className="flex justify-center bg-black px-4 py-4">
              <div
                className={`relative w-full overflow-hidden rounded-lg ${config.previewClass}`}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              </div>
            </div>
            <div className="flex gap-3 border-t border-gray-100 bg-white px-5 py-4">
              <button
                type="button"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-UbuntuMedium text-gray-800 shadow-sm transition hover:bg-gray-50"
                onClick={closeCamera}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-UbuntuMedium text-white shadow-sm transition hover:opacity-90"
                onClick={handleCapture}
              >
                Capture
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CameraCapture;
